import { Tool } from '@modelcontextprotocol/sdk/types.js';
import Handlebars from 'handlebars';
import {
  ScaffoldExtensionInputSchema,
  type Config,
  type ScaffoldResult,
} from '../types/index.js';
import { writeText, ensureDirectory, validatePathSecurity } from '../utils/fs.js';
import { findSmartStackStructure } from '../lib/detector.js';
import { logger } from '../lib/logger.js';
import path from 'path';

export const scaffoldExtensionTool: Tool = {
  name: 'scaffold_extension',
  description: 'Generate code to extend SmartStack: feature (full-stack), entity, service, controller, component, dto, validator, repository, or test',
  inputSchema: {
    type: 'object',
    properties: {
      type: {
        type: 'string',
        enum: ['feature', 'service', 'entity', 'controller', 'component', 'test', 'dto', 'validator', 'repository'],
        description: 'Type of extension to scaffold. Use "feature" for full-stack generation.',
      },
      name: {
        type: 'string',
        description: 'Name of the extension (e.g., "UserProfile", "Order")',
      },
      options: {
        type: 'object',
        properties: {
          namespace: {
            type: 'string',
            description: 'Custom namespace (optional)',
          },
          baseEntity: {
            type: 'string',
            description: 'Base entity to extend (for entity type)',
          },
          methods: {
            type: 'array',
            items: { type: 'string' },
            description: 'Methods to generate (for service type)',
          },
          outputPath: {
            type: 'string',
            description: 'Custom output path',
          },
          isSystemEntity: {
            type: 'boolean',
            description: 'If true, creates a system entity without TenantId (for entity type)',
          },
          tablePrefix: {
            type: 'string',
            description: 'Domain prefix for table name (e.g., "auth_", "nav_", "cfg_")',
          },
          schema: {
            type: 'string',
            enum: ['core', 'extensions'],
            description: 'Database schema (default: core)',
          },
          dryRun: {
            type: 'boolean',
            description: 'If true, preview generated code without writing files',
          },
          skipService: {
            type: 'boolean',
            description: 'For feature type: skip service generation',
          },
          skipController: {
            type: 'boolean',
            description: 'For feature type: skip controller generation',
          },
          skipComponent: {
            type: 'boolean',
            description: 'For feature type: skip React component generation',
          },
          clientExtension: {
            type: 'boolean',
            description: 'If true, use extensions schema for client-specific code',
          },
          withTests: {
            type: 'boolean',
            description: 'For feature type: also generate unit tests',
          },
          withDtos: {
            type: 'boolean',
            description: 'For feature type: generate DTOs (Create, Update, Response)',
          },
          withValidation: {
            type: 'boolean',
            description: 'For feature type: generate FluentValidation validators',
          },
          withRepository: {
            type: 'boolean',
            description: 'For feature type: generate repository pattern',
          },
          withSeedData: {
            type: 'boolean',
            description: 'For entity type: generate centralized SeedData file in Seeding/Data/{Domain}/',
          },
          entityProperties: {
            type: 'array',
            items: {
              type: 'object',
              properties: {
                name: { type: 'string' },
                type: { type: 'string' },
                required: { type: 'boolean' },
                maxLength: { type: 'number' },
              },
            },
            description: 'Entity properties for DTO/Validator generation',
          },
          navRoute: {
            type: 'string',
            description: 'Navigation route path for controller (e.g., "platform.administration.users"). Required for controllers.',
          },
          navRouteSuffix: {
            type: 'string',
            description: 'Optional suffix for NavRoute (e.g., "dashboard" for sub-resources)',
          },
          withHierarchyFunction: {
            type: 'boolean',
            description: 'For entity type with self-reference (ParentId): generate TVF SQL script for hierarchy traversal',
          },
          hierarchyDirection: {
            type: 'string',
            enum: ['ancestors', 'descendants', 'both'],
            description: 'Direction for hierarchy traversal function (default: both)',
          },
        },
      },
    },
    required: ['type', 'name'],
  },
};

// Register Handlebars helpers
Handlebars.registerHelper('pascalCase', (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
});

Handlebars.registerHelper('camelCase', (str: string) => {
  return str.charAt(0).toLowerCase() + str.slice(1);
});

Handlebars.registerHelper('kebabCase', (str: string) => {
  return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
});

/**
 * Resolves navRoute hierarchy into folder segments for organized file placement.
 * navRoute format: "context.application.module[.section]"
 * Example: "platform.administration.tenants" → { context: "Platform", application: "Administration", module: "Tenants" }
 *
 * When navRoute is absent, returns empty strings → backward-compatible flat paths.
 */
function resolveHierarchy(navRoute?: string): {
  context: string;
  application: string;
  module: string;
  /** Subfolder for Domain/Application layers: Context/Application/Module */
  domainPath: string;
  /** Subfolder for Infrastructure layers: Module only */
  infraPath: string;
  /** Subfolder for Controller area: Context */
  controllerArea: string;
} {
  if (!navRoute) {
    return { context: '', application: '', module: '', domainPath: '', infraPath: '', controllerArea: '' };
  }

  const segments = navRoute.split('.');
  const toPascal = (s: string) =>
    s.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');

  const context = segments[0] ? toPascal(segments[0]) : '';
  const application = segments[1] ? toPascal(segments[1]) : '';
  const module = segments[2] ? toPascal(segments[2]) : segments[1] ? toPascal(segments[1]) : '';

  // Domain/Application: full hierarchy
  let domainPath = '';
  if (segments.length >= 3) {
    domainPath = path.join(context, application, module);
  } else if (segments.length === 2) {
    domainPath = path.join(context, module);
  } else if (segments.length === 1) {
    domainPath = context;
  }

  // Infrastructure: module-level grouping
  const infraPath = module || '';

  // Controller: context as area
  const controllerArea = context;

  return { context, application, module, domainPath, infraPath, controllerArea };
}

export async function handleScaffoldExtension(
  args: unknown,
  config: Config
): Promise<string> {
  const input = ScaffoldExtensionInputSchema.parse(args);
  const dryRun = input.options?.dryRun || false;

  logger.info('Scaffolding extension', { type: input.type, name: input.name, dryRun });

  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  const result: ScaffoldResult = {
    success: true,
    files: [],
    instructions: [],
  };

  // Handle clientExtension option - override schema
  if (input.options?.clientExtension) {
    input.options.schema = 'extensions';
    // Note: tablePrefix should be specified based on domain (e.g., 'ref_', 'cfg_')
    // No longer using scope-based prefixes (core_, ext_) - using Scope field instead
  }

  try {
    switch (input.type) {
      case 'feature':
        await scaffoldFeature(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'service':
        await scaffoldService(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'entity':
        await scaffoldEntity(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'controller':
        await scaffoldController(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'component':
        await scaffoldComponent(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'test':
        await scaffoldTest(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'dto':
        await scaffoldDtos(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'validator':
        await scaffoldValidator(input.name, input.options, structure, config, result, dryRun);
        break;
      case 'repository':
        await scaffoldRepository(input.name, input.options, structure, config, result, dryRun);
        break;
    }
  } catch (error) {
    result.success = false;
    result.instructions.push(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return formatResult(result, input.type, input.name, dryRun);
}

// Entity property definition for DTO/Validator generation
interface EntityProperty {
  name: string;
  type: string;
  required?: boolean;
  maxLength?: number;
}

// Define extended options type for feature scaffolding
interface FeatureOptions {
  namespace?: string;
  baseEntity?: string;
  methods?: string[];
  outputPath?: string;
  isSystemEntity?: boolean;
  tablePrefix?: string;
  schema?: 'core' | 'extensions';
  dryRun?: boolean;
  skipService?: boolean;
  skipController?: boolean;
  skipComponent?: boolean;
  clientExtension?: boolean;
  withTests?: boolean;
  withDtos?: boolean;
  withValidation?: boolean;
  withRepository?: boolean;
  withSeedData?: boolean;
  seedDataDomain?: string;
  entityProperties?: EntityProperty[];
  navRoute?: string;
  navRouteSuffix?: string;
  withHierarchyFunction?: boolean;
  hierarchyDirection?: 'ancestors' | 'descendants' | 'both';
}

async function scaffoldFeature(
  name: string,
  options: FeatureOptions | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const skipService = options?.skipService || false;
  const skipController = options?.skipController || false;
  const skipComponent = options?.skipComponent || false;
  const isClientExtension = options?.clientExtension || false;
  const withTests = options?.withTests || false;
  const withDtos = options?.withDtos || false;
  const withValidation = options?.withValidation || false;
  const withRepository = options?.withRepository || false;

  let stepNumber = 1;

  // Add feature header to instructions
  result.instructions.push(`# Full-Stack Feature: ${name}`);
  result.instructions.push('');
  if (isClientExtension) {
    result.instructions.push('> **Client Extension**: Using `extensions` schema and `ext_` prefix');
    result.instructions.push('');
  }

  // 1. Always generate entity (it's the foundation)
  result.instructions.push(`## ${stepNumber}. Domain Entity`);
  await scaffoldEntity(name, options, structure, config, result, dryRun);
  result.instructions.push('');
  stepNumber++;

  // 2. Generate DTOs (if requested)
  if (withDtos) {
    result.instructions.push(`## ${stepNumber}. DTOs (Data Transfer Objects)`);
    await scaffoldDtos(name, options, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 3. Generate validators (if requested)
  if (withValidation) {
    result.instructions.push(`## ${stepNumber}. FluentValidation Validators`);
    await scaffoldValidator(name, options, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 4. Generate repository (if requested)
  if (withRepository) {
    result.instructions.push(`## ${stepNumber}. Repository Pattern`);
    await scaffoldRepository(name, options, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 5. Generate service (unless skipped)
  if (!skipService) {
    result.instructions.push(`## ${stepNumber}. Application Service`);
    // Create service with entity-aware methods
    const serviceMethods = [
      `GetByIdAsync`,
      `GetAllAsync`,
      `CreateAsync`,
      `UpdateAsync`,
      `DeleteAsync`,
    ];
    await scaffoldService(name, { ...options, methods: serviceMethods }, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 6. Generate controller (unless skipped)
  if (!skipController) {
    result.instructions.push(`## ${stepNumber}. API Controller`);
    await scaffoldController(name, options, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 7. Generate component (unless skipped)
  if (!skipComponent) {
    result.instructions.push(`## ${stepNumber}. React Component`);
    await scaffoldComponent(name, options, structure, config, result, dryRun);
    result.instructions.push('');
    stepNumber++;
  }

  // 8. Generate tests (if requested)
  if (withTests && !skipService) {
    result.instructions.push(`## ${stepNumber}. Unit Tests`);
    await scaffoldTest(name, options, structure, config, result, dryRun);
    result.instructions.push('');
  }

  // Add summary
  const generated: string[] = ['Entity'];
  if (withDtos) generated.push('DTOs');
  if (withValidation) generated.push('Validators');
  if (withRepository) generated.push('Repository');
  if (!skipService) generated.push('Service');
  if (!skipController) generated.push('Controller');
  if (!skipComponent) generated.push('Component');
  if (withTests && !skipService) generated.push('Tests');

  result.instructions.push('---');
  result.instructions.push(`## Summary: Generated ${generated.join(' + ')}`);
  result.instructions.push('');

  // Determine DbContext based on schema/extension type
  // Priority: explicit schema > clientExtension flag > project config default
  const schema = options?.schema || (isClientExtension ? 'extensions' : config.defaultDbContext);
  const dbContextName = schema === 'extensions' ? 'ExtensionsDbContext' : 'CoreDbContext';
  const dbContextInterface = schema === 'extensions' ? 'IExtensionsDbContext' : 'ICoreDbContext';
  const migrationPrefix = schema; // Use full context name: 'core' or 'extensions'

  result.instructions.push('### Next Steps:');
  result.instructions.push(`1. Add DbSet to ${dbContextInterface} and ${dbContextName}: \`public DbSet<${name}> ${name}s => Set<${name}>();\``);
  if (withRepository) {
    result.instructions.push(`2. Register repository: \`services.AddScoped<I${name}Repository, ${name}Repository>();\``);
  }
  result.instructions.push(`${withRepository ? '3' : '2'}. Register service: \`services.AddScoped<I${name}Service, ${name}Service>();\``);
  if (withValidation) {
    result.instructions.push(`${withRepository ? '4' : '3'}. Register validators: \`services.AddValidatorsFromAssemblyContaining<Create${name}DtoValidator>();\``);
  }
  result.instructions.push(`${withRepository ? (withValidation ? '5' : '4') : (withValidation ? '4' : '3')}. Create migration: \`dotnet ef migrations add ${migrationPrefix}_vX.X.X_XXX_Add${name} --context ${dbContextName}\``);
  result.instructions.push(`${withRepository ? (withValidation ? '6' : '5') : (withValidation ? '5' : '4')}. Run migration: \`dotnet ef database update --context ${dbContextName}\``);
  if (!skipComponent) {
    const featureHierarchy = resolveHierarchy(options?.navRoute);
    const featureComponentPath = featureHierarchy.context && featureHierarchy.module
      ? `@/components/${featureHierarchy.context.toLowerCase()}/${featureHierarchy.module.toLowerCase()}/${name}`
      : `./components/${name}`;
    result.instructions.push(`Import component: \`import { ${name} } from '${featureComponentPath}';\``);
  }
}

async function scaffoldService(
  name: string,
  options: { namespace?: string; methods?: string[]; dryRun?: boolean; navRoute?: string } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const interfaceNamespace = options?.namespace || `${config.conventions.namespaces.application}.Common.Interfaces`;
  const implNamespace = hierarchy.infraPath
    ? `${config.conventions.namespaces.infrastructure}.Services.${hierarchy.infraPath}`
    : `${config.conventions.namespaces.infrastructure}.Services`;
  const methods = options?.methods || ['GetByIdAsync', 'GetAllAsync', 'CreateAsync', 'UpdateAsync', 'DeleteAsync'];

  // Interface template — placed in Application/Common/Interfaces/
  const interfaceTemplate = `using System.Threading;
using System.Threading.Tasks;
using System.Collections.Generic;

namespace {{interfaceNamespace}};

/// <summary>
/// Service interface for {{name}} operations
/// </summary>
public interface I{{name}}Service
{
{{#each methods}}
    /// <summary>
    /// {{this}} operation
    /// </summary>
    Task<object> {{this}}(CancellationToken cancellationToken = default);

{{/each}}
}
`;

  // Implementation template — placed in Infrastructure/Services/{Module}/
  const implementationTemplate = `using System.Threading;
using System.Threading.Tasks;
using System.Collections.Generic;
using Microsoft.Extensions.Logging;

namespace {{implNamespace}};

/// <summary>
/// Service implementation for {{name}} operations
/// </summary>
public class {{name}}Service : I{{name}}Service
{
    private readonly ILogger<{{name}}Service> _logger;

    public {{name}}Service(ILogger<{{name}}Service> logger)
    {
        _logger = logger;
    }

{{#each methods}}
    /// <inheritdoc />
    public async Task<object> {{this}}(CancellationToken cancellationToken = default)
    {
        _logger.LogInformation("Executing {{this}}");
        // TODO: Implement {{this}}
        await Task.CompletedTask;
        throw new NotImplementedException();
    }

{{/each}}
}
`;

  // DI registration template
  const diTemplate = `// Add to DependencyInjection.cs or ServiceCollectionExtensions.cs:
services.AddScoped<I{{name}}Service, {{name}}Service>();
`;

  const context = { interfaceNamespace, implNamespace, name, methods };

  const interfaceContent = Handlebars.compile(interfaceTemplate)(context);
  const implementationContent = Handlebars.compile(implementationTemplate)(context);
  const diContent = Handlebars.compile(diTemplate)(context);

  // Determine output paths — interface in Application/Common/Interfaces/, impl in Infrastructure/Services/{Module}/
  const projectRoot = config.smartstack.projectPath;
  const appPath = structure.application || projectRoot;
  const infraPath = structure.infrastructure || path.join(projectRoot, 'Infrastructure');

  const interfacesDir = path.join(appPath, 'Common', 'Interfaces');
  const implDir = hierarchy.infraPath
    ? path.join(infraPath, 'Services', hierarchy.infraPath)
    : path.join(infraPath, 'Services');

  const interfacePath = path.join(interfacesDir, `I${name}Service.cs`);
  const implementationPath = path.join(implDir, `${name}Service.cs`);

  // Validate paths don't escape project directory
  validatePathSecurity(interfacePath, projectRoot);
  validatePathSecurity(implementationPath, projectRoot);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(interfacesDir);
    await ensureDirectory(implDir);
    await writeText(interfacePath, interfaceContent);
    await writeText(implementationPath, implementationContent);
  }

  result.files.push({ path: interfacePath, content: interfaceContent, type: 'created' });
  result.files.push({ path: implementationPath, content: implementationContent, type: 'created' });

  result.instructions.push('Register service in DI container:');
  result.instructions.push(diContent);
}

async function scaffoldEntity(
  name: string,
  options: { namespace?: string; baseEntity?: string; isSystemEntity?: boolean; tablePrefix?: string; schema?: string; dryRun?: boolean; withSeedData?: boolean; seedDataDomain?: string; withHierarchyFunction?: boolean; hierarchyDirection?: 'ancestors' | 'descendants' | 'both'; navRoute?: string } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const namespace = options?.namespace || (hierarchy.domainPath
    ? `${config.conventions.namespaces.domain}.${hierarchy.domainPath.replace(/[\\/]/g, '.')}`
    : config.conventions.namespaces.domain);
  const baseEntity = options?.baseEntity;
  const isSystemEntity = options?.isSystemEntity || false;
  const tablePrefix = options?.tablePrefix || 'ref_';
  const schema = options?.schema || config.conventions.schemas.platform;

  // Entity template with full conventions (TenantId, Code, SoftDelete, RowVersion)
  // Default is tenant-aware (ITenantEntity), use isSystemEntity: true for platform-level entities
  const entityTemplate = `using System;
using SmartStack.Domain.Common;
using SmartStack.Domain.Common.Interfaces;

namespace {{namespace}};

/// <summary>
/// {{name}} entity{{#if baseEntity}} extending {{baseEntity}}{{/if}}
{{#unless isSystemEntity}}
/// Tenant-scoped: data is isolated per tenant
{{else}}
/// System entity: platform-level, no tenant isolation
{{/unless}}
/// </summary>
{{#if isSystemEntity}}
public class {{name}} : SystemEntity
{{else}}
public class {{name}} : BaseEntity, ITenantEntity
{{/if}}
{
{{#unless isSystemEntity}}
    // === MULTI-TENANT ===

    /// <summary>
    /// Tenant identifier for multi-tenant isolation (required)
    /// </summary>
    public Guid TenantId { get; private set; }

{{/unless}}
{{#if baseEntity}}
    // === RELATIONSHIPS ===

    /// <summary>
    /// Foreign key to {{baseEntity}}
    /// </summary>
    public Guid {{baseEntity}}Id { get; private set; }

    /// <summary>
    /// Navigation property to {{baseEntity}}
    /// </summary>
    public virtual {{baseEntity}}? {{baseEntity}} { get; private set; }

{{/if}}
    // === BUSINESS PROPERTIES ===
    // TODO: Add {{name}} specific properties here

    /// <summary>
    /// Private constructor for EF Core
    /// </summary>
    private {{name}}() { }

    /// <summary>
    /// Factory method to create a new {{name}}
    /// </summary>
{{#if isSystemEntity}}
    public static {{name}} Create(
        string code,
        string? createdBy = null)
    {
        return new {{name}}
        {
            Id = Guid.NewGuid(),
            Code = code.ToLowerInvariant(),
            CreatedAt = DateTime.UtcNow,
            CreatedBy = createdBy
        };
    }
{{else}}
    /// <param name="tenantId">Required tenant identifier</param>
    /// <param name="code">Unique code within tenant (will be lowercased)</param>
    /// <param name="createdBy">User who created this entity</param>
    public static {{name}} Create(
        Guid tenantId,
        string code,
        string? createdBy = null)
    {
        if (tenantId == Guid.Empty)
            throw new ArgumentException("TenantId is required", nameof(tenantId));

        return new {{name}}
        {
            Id = Guid.NewGuid(),
            TenantId = tenantId,
            Code = code.ToLowerInvariant(),
            CreatedAt = DateTime.UtcNow,
            CreatedBy = createdBy
        };
    }
{{/if}}

    /// <summary>
    /// Update the entity
    /// </summary>
    public void Update(string? updatedBy = null)
    {
        UpdatedAt = DateTime.UtcNow;
        UpdatedBy = updatedBy;
    }

    /// <summary>
    /// Soft delete the entity
    /// </summary>
    public void SoftDelete(string? deletedBy = null)
    {
        IsDeleted = true;
        DeletedAt = DateTime.UtcNow;
        DeletedBy = deletedBy;
    }

    /// <summary>
    /// Restore a soft-deleted entity
    /// </summary>
    public void Restore(string? restoredBy = null)
    {
        IsDeleted = false;
        DeletedAt = null;
        DeletedBy = null;
        UpdatedAt = DateTime.UtcNow;
        UpdatedBy = restoredBy;
    }
}
`;

  // EF Configuration template with full conventions
  const configTemplate = `using Microsoft.EntityFrameworkCore;
using Microsoft.EntityFrameworkCore.Metadata.Builders;
using {{domainNamespace}};

namespace {{infrastructureNamespace}}.Persistence.Configurations;

/// <summary>
/// EF Core configuration for {{name}}
{{#unless isSystemEntity}}
/// Tenant-aware: includes tenant isolation query filter
{{else}}
/// System entity: no tenant isolation
{{/unless}}
/// </summary>
public class {{name}}Configuration : IEntityTypeConfiguration<{{name}}>
{
    public void Configure(EntityTypeBuilder<{{name}}> builder)
    {
        // Table name with schema and domain prefix
        builder.ToTable("{{tablePrefix}}{{name}}s", "{{schema}}");

        // Primary key
        builder.HasKey(e => e.Id);

{{#unless isSystemEntity}}
        // ============================================
        // MULTI-TENANT CONFIGURATION
        // ============================================

        // TenantId is required for tenant isolation
        builder.Property(e => e.TenantId).IsRequired();
        builder.HasIndex(e => e.TenantId)
            .HasDatabaseName("IX_{{tablePrefix}}{{name}}s_TenantId");

        // Tenant relationship (configured in Tenant configuration)
        // builder.HasOne<Tenant>().WithMany().HasForeignKey(e => e.TenantId);

        // Code: lowercase, unique per tenant (filtered for soft delete)
        builder.Property(e => e.Code).HasMaxLength(100).IsRequired();
        builder.HasIndex(e => new { e.TenantId, e.Code })
            .IsUnique()
            .HasFilter("[IsDeleted] = 0")
            .HasDatabaseName("IX_{{tablePrefix}}{{name}}s_Tenant_Code_Unique");
{{else}}
        // Code: lowercase, unique globally (filtered for soft delete)
        builder.Property(e => e.Code).HasMaxLength(100).IsRequired();
        builder.HasIndex(e => e.Code)
            .IsUnique()
            .HasFilter("[IsDeleted] = 0")
            .HasDatabaseName("IX_{{tablePrefix}}{{name}}s_Code_Unique");
{{/unless}}

        // Concurrency token
        builder.Property(e => e.RowVersion).IsRowVersion();

        // Audit fields
        builder.Property(e => e.CreatedBy).HasMaxLength(256);
        builder.Property(e => e.UpdatedBy).HasMaxLength(256);
        builder.Property(e => e.DeletedBy).HasMaxLength(256);

{{#if baseEntity}}
        // ============================================
        // RELATIONSHIPS
        // ============================================

        // Relationship to {{baseEntity}} (1:1)
        builder.HasOne(e => e.{{baseEntity}})
               .WithOne()
               .HasForeignKey<{{name}}>(e => e.{{baseEntity}}Id)
               .OnDelete(DeleteBehavior.Cascade);

        // Index on foreign key
        builder.HasIndex(e => e.{{baseEntity}}Id)
               .IsUnique();
{{/if}}

        // ============================================
        // QUERY FILTERS
        // ============================================
        // Note: Global query filters are applied in DbContext.OnModelCreating
        // - Soft delete: .HasQueryFilter(e => !e.IsDeleted)
{{#unless isSystemEntity}}
        // - Tenant isolation: .HasQueryFilter(e => e.TenantId == _tenantId)
        // Combined filter applied via ITenantEntity interface check
{{/unless}}

        // TODO: Add additional business-specific configuration
    }
}
`;

  const context = {
    namespace,
    name,
    baseEntity,
    isSystemEntity,
    tablePrefix,
    schema,
    infrastructureNamespace: config.conventions.namespaces.infrastructure,
    domainNamespace: config.conventions.namespaces.domain,
  };

  const entityContent = Handlebars.compile(entityTemplate)(context);
  const configContent = Handlebars.compile(configTemplate)(context);

  // Determine output paths — entity in Domain/{Context}/{App}/{Module}/, config in Configurations/{Module}/
  const projectRoot = config.smartstack.projectPath;
  const domainBase = structure.domain || path.join(projectRoot, 'Domain');
  const infraBase = structure.infrastructure || path.join(projectRoot, 'Infrastructure');

  const entityDir = hierarchy.domainPath
    ? path.join(domainBase, hierarchy.domainPath)
    : domainBase;
  const configDir = hierarchy.infraPath
    ? path.join(infraBase, 'Persistence', 'Configurations', hierarchy.infraPath)
    : path.join(infraBase, 'Persistence', 'Configurations');

  const entityFilePath = path.join(entityDir, `${name}.cs`);
  const configFilePath = path.join(configDir, `${name}Configuration.cs`);

  // Validate paths don't escape project directory
  validatePathSecurity(entityFilePath, projectRoot);
  validatePathSecurity(configFilePath, projectRoot);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(entityDir);
    await ensureDirectory(configDir);
    await writeText(entityFilePath, entityContent);
    await writeText(configFilePath, configContent);
  }

  result.files.push({ path: entityFilePath, content: entityContent, type: 'created' });
  result.files.push({ path: configFilePath, content: configContent, type: 'created' });

  // Determine DbContext based on schema
  const dbContextName = schema === 'extensions' ? 'ExtensionsDbContext' : 'CoreDbContext';
  const dbContextInterface = schema === 'extensions' ? 'IExtensionsDbContext' : 'ICoreDbContext';
  const migrationPrefix = schema; // Use full context name: 'core' or 'extensions'

  result.instructions.push(`Add DbSet to ${dbContextInterface}:`);
  result.instructions.push(`public DbSet<${name}> ${name}s => Set<${name}>();`);
  result.instructions.push('');
  result.instructions.push(`And implementation in ${dbContextName}:`);
  result.instructions.push(`public DbSet<${name}> ${name}s => Set<${name}>();`);
  result.instructions.push('');
  result.instructions.push('Create migration:');
  result.instructions.push(`dotnet ef migrations add ${migrationPrefix}_vX.X.X_XXX_Add${name} --context ${dbContextName}`);
  result.instructions.push('');
  result.instructions.push('Required fields from BaseEntity:');
  result.instructions.push(`- Id (Guid), ${isSystemEntity ? '' : 'TenantId (Guid), '}Code (string, lowercase)`);
  result.instructions.push('- CreatedAt, UpdatedAt, CreatedBy, UpdatedBy (audit)');
  result.instructions.push('- IsDeleted, DeletedAt, DeletedBy (soft delete)');
  result.instructions.push('- RowVersion (concurrency)');

  // Generate SeedData file if requested
  if (options?.withSeedData) {
    result.instructions.push('');
    result.instructions.push('### Seed Data');
    await scaffoldSeedData(name, options, structure, config, result, dryRun);
  }

  // Generate TVF hierarchy function if requested
  if (options?.withHierarchyFunction) {
    result.instructions.push('');
    result.instructions.push('### Hierarchy Function (TVF)');
    await scaffoldHierarchyFunction(name, options, structure, config, result, dryRun);
  }
}

async function scaffoldSeedData(
  name: string,
  options: { seedDataDomain?: string; tablePrefix?: string; isSystemEntity?: boolean; dryRun?: boolean } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  // Determine domain folder based on tablePrefix or explicit seedDataDomain
  const tablePrefix = options?.tablePrefix || 'ref_';
  const domainMap: Record<string, string> = {
    'auth_': 'Authorization',
    'nav_': 'Navigation',
    'usr_': 'User',
    'wkf_': 'Communications',
    'cfg_': 'Configuration',
    'ai_': 'AI',
    'entra_': 'Entra',
    'ref_': 'Reference',
    'support_': 'Support',
    'loc_': 'Localization',
  };
  const domain = options?.seedDataDomain || domainMap[tablePrefix] || 'Reference';
  const isSystemEntity = options?.isSystemEntity || false;

  // SeedData template following the centralized pattern
  const seedDataTemplate = `using SmartStack.Domain.{{domainNamespace}};

namespace SmartStack.Infrastructure.Persistence.Seeding.Data.{{domain}};

/// <summary>
/// Donnees seed pour {{name}}.
/// Centralise les IDs et donnees d'initialisation.
/// </summary>
public static class {{name}}SeedData
{
    // ============================================================
    // IDs - Documenter chaque ID avec son role
    // ============================================================

    /// <summary>ID exemple - A remplacer par vos IDs</summary>
    public static readonly Guid ExampleId = Guid.Parse("{{exampleGuid}}");

    // ============================================================
    // CODES / CONSTANTS
    // ============================================================

    public const string ExampleCode = "example";

    // ============================================================
    // SEED DATA
    // ============================================================

    /// <summary>
    /// Retourne toutes les donnees seed pour {{name}}.
    /// Appelé depuis {{name}}Configuration.HasData()
    /// </summary>
    public static object[] GetSeedData()
    {
        var seedDate = SeedConstants.SeedDate;

        return new object[]
        {
            // Exemple - A remplacer par vos donnees
            new
            {
                Id = ExampleId,
{{#unless isSystemEntity}}
                TenantId = (Guid?)null, // Seed data systeme sans tenant
{{/unless}}
                Code = ExampleCode,
                // TODO: Ajouter les proprietes specifiques
                IsDeleted = false,
                CreatedAt = seedDate
            }
        };
    }
}
`;

  // Generate a random GUID for the example
  const exampleGuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });

  const context = {
    name,
    domain,
    domainNamespace: domain,
    isSystemEntity,
    exampleGuid,
  };

  const seedDataContent = Handlebars.compile(seedDataTemplate)(context);

  // Determine output path: Infrastructure/Persistence/Seeding/Data/{Domain}/{Name}SeedData.cs
  const projectRoot = config.smartstack.projectPath;
  const infraPath = structure.infrastructure || path.join(projectRoot, 'Infrastructure');
  const seedDataPath = path.join(infraPath, 'Persistence', 'Seeding', 'Data', domain);
  const seedDataFilePath = path.join(seedDataPath, `${name}SeedData.cs`);

  // Validate paths don't escape project directory
  validatePathSecurity(seedDataFilePath, projectRoot);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(seedDataPath);
    await writeText(seedDataFilePath, seedDataContent);
  }

  result.files.push({ path: seedDataFilePath, content: seedDataContent, type: 'created' });

  result.instructions.push(`SeedData file generated in Seeding/Data/${domain}/`);
  result.instructions.push('');
  result.instructions.push('Update your Configuration to use centralized SeedData:');
  result.instructions.push('```csharp');
  result.instructions.push(`// In ${name}Configuration.cs`);
  result.instructions.push(`using SmartStack.Infrastructure.Persistence.Seeding.Data.${domain};`);
  result.instructions.push('');
  result.instructions.push(`builder.HasData(${name}SeedData.GetSeedData());`);
  result.instructions.push('```');
  result.instructions.push('');
  result.instructions.push('Pattern to follow:');
  result.instructions.push('1. Define public static readonly Guid IDs');
  result.instructions.push('2. Define const string codes');
  result.instructions.push('3. Implement GetSeedData() returning object[]');
  result.instructions.push('4. Reference other SeedData IDs for foreign keys');
}

async function scaffoldHierarchyFunction(
  name: string,
  options: { tablePrefix?: string; schema?: string; hierarchyDirection?: 'ancestors' | 'descendants' | 'both'; dryRun?: boolean } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const tablePrefix = options?.tablePrefix || 'ref_';
  const schema = options?.schema || config.conventions.schemas.platform;
  // hierarchyDirection reserved for future use to generate different TVF variants
  const tableName = `${tablePrefix}${name}s`;

  // SQL TVF Template
  const tvfTemplate = `-- ============================================================================
-- Table-Valued Function: fn_Get{{name}}Hierarchy
-- Purpose: Recursively traverse {{name}} hierarchy (ancestors/descendants)
-- Generated by: SmartStack MCP scaffold_extension
-- ============================================================================

-- Drop existing function if it exists
IF OBJECT_ID('[{{schema}}].[fn_Get{{name}}Hierarchy]', 'IF') IS NOT NULL
    DROP FUNCTION [{{schema}}].[fn_Get{{name}}Hierarchy];
GO

CREATE FUNCTION [{{schema}}].[fn_Get{{name}}Hierarchy]
(
    @{{nameLower}}Id UNIQUEIDENTIFIER,
    @Direction VARCHAR(11) = 'descendants' -- 'ancestors', 'descendants', or 'both'
)
RETURNS TABLE
AS
RETURN
(
    WITH HierarchyCTE AS (
        -- ========================================
        -- Base case: starting node
        -- ========================================
        SELECT
            Id,
            ParentId,
            Code,
            TenantId,
            0 AS [Level],
            CAST(Id AS NVARCHAR(MAX)) AS [Path],
            'self' AS Direction
        FROM [{{schema}}].[{{tableName}}]
        WHERE Id = @{{nameLower}}Id
          AND IsDeleted = 0

        UNION ALL

        -- ========================================
        -- Recursive: ancestors (climb up the tree)
        -- ========================================
        SELECT
            parent.Id,
            parent.ParentId,
            parent.Code,
            parent.TenantId,
            h.[Level] - 1,
            CAST(parent.Id AS NVARCHAR(MAX)) + '/' + h.[Path],
            'ancestor' AS Direction
        FROM [{{schema}}].[{{tableName}}] parent
        INNER JOIN HierarchyCTE h ON parent.Id = h.ParentId
        WHERE parent.IsDeleted = 0
          AND @Direction IN ('ancestors', 'both')
          AND h.[Level] > -50  -- Prevent infinite recursion

        UNION ALL

        -- ========================================
        -- Recursive: descendants (go down the tree)
        -- ========================================
        SELECT
            child.Id,
            child.ParentId,
            child.Code,
            child.TenantId,
            h.[Level] + 1,
            h.[Path] + '/' + CAST(child.Id AS NVARCHAR(MAX)),
            'descendant' AS Direction
        FROM [{{schema}}].[{{tableName}}] child
        INNER JOIN HierarchyCTE h ON child.ParentId = h.Id
        WHERE child.IsDeleted = 0
          AND @Direction IN ('descendants', 'both')
          AND h.[Level] < 50  -- Prevent infinite recursion
    )
    SELECT
        Id,
        ParentId,
        Code,
        TenantId,
        [Level],
        [Path],
        Direction,
        ABS([Level]) AS Depth  -- Absolute depth from starting node
    FROM HierarchyCTE
    WHERE (
        @Direction = 'both'
        OR (@Direction = 'ancestors' AND Direction IN ('self', 'ancestor'))
        OR (@Direction = 'descendants' AND Direction IN ('self', 'descendant'))
    )
);
GO

-- ============================================================================
-- Usage Examples
-- ============================================================================

-- Get all descendants of a {{name}}
-- SELECT * FROM [{{schema}}].[fn_Get{{name}}Hierarchy](@id, 'descendants') ORDER BY [Level];

-- Get all ancestors of a {{name}}
-- SELECT * FROM [{{schema}}].[fn_Get{{name}}Hierarchy](@id, 'ancestors') ORDER BY [Level] DESC;

-- Get full hierarchy (ancestors + descendants)
-- SELECT * FROM [{{schema}}].[fn_Get{{name}}Hierarchy](@id, 'both') ORDER BY [Level];

-- Get all {{name}}s at a specific level
-- SELECT * FROM [{{schema}}].[fn_Get{{name}}Hierarchy](@rootId, 'descendants') WHERE [Level] = 2;

-- Check if user belongs to a specific {{name}} hierarchy
-- SELECT CASE WHEN EXISTS (
--     SELECT 1 FROM [{{schema}}].[fn_Get{{name}}Hierarchy](@userId{{name}}Id, 'ancestors')
--     WHERE Id = @target{{name}}Id
-- ) THEN 1 ELSE 0 END AS BelongsToHierarchy;
`;

  const context = {
    name,
    nameLower: name.charAt(0).toLowerCase() + name.slice(1),
    tableName,
    schema,
  };

  const tvfContent = Handlebars.compile(tvfTemplate)(context);

  // Determine output path
  const projectRoot = config.smartstack.projectPath;
  const infraPath = structure.infrastructure || path.join(projectRoot, 'Infrastructure');
  const scriptsPath = path.join(infraPath, 'Persistence', 'Scripts', 'Functions');
  const tvfFilePath = path.join(scriptsPath, `fn_Get${name}Hierarchy.sql`);

  // Validate paths
  validatePathSecurity(tvfFilePath, projectRoot);

  // Write file (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(scriptsPath);
    await writeText(tvfFilePath, tvfContent);
  }

  result.files.push({ path: tvfFilePath, content: tvfContent, type: 'created' });

  result.instructions.push('TVF hierarchy function generated!');
  result.instructions.push('');
  result.instructions.push('**Next Steps:**');
  result.instructions.push(`1. Run the SQL script to create the function in your database`);
  result.instructions.push(`2. Add a method to I${name}Repository for hierarchy queries:`);
  result.instructions.push('```csharp');
  result.instructions.push(`Task<IReadOnlyList<${name}HierarchyDto>> GetHierarchyAsync(`);
  result.instructions.push(`    Guid ${name.toLowerCase()}Id,`);
  result.instructions.push(`    HierarchyDirection direction,`);
  result.instructions.push(`    CancellationToken ct = default);`);
  result.instructions.push('```');
  result.instructions.push('');
  result.instructions.push(`3. Create ${name}HierarchyDto:`);
  result.instructions.push('```csharp');
  result.instructions.push(`public record ${name}HierarchyDto(`);
  result.instructions.push('    Guid Id,');
  result.instructions.push('    Guid? ParentId,');
  result.instructions.push('    string Code,');
  result.instructions.push('    Guid TenantId,');
  result.instructions.push('    int Level,');
  result.instructions.push('    string Path,');
  result.instructions.push('    string Direction,');
  result.instructions.push('    int Depth);');
  result.instructions.push('```');
  result.instructions.push('');
  result.instructions.push('4. Implement using raw SQL in repository:');
  result.instructions.push('```csharp');
  result.instructions.push(`var sql = "SELECT * FROM [${schema}].[fn_Get${name}Hierarchy]({0}, {1})";`);
  result.instructions.push(`return await _context.Database`);
  result.instructions.push(`    .SqlQueryRaw<${name}HierarchyDto>(sql, id, direction.ToString().ToLower())`);
  result.instructions.push(`    .ToListAsync(ct);`);
  result.instructions.push('```');
  result.instructions.push('');
  result.instructions.push(`**Pattern Recommendation**: TVF with CTE is optimal for:`);
  result.instructions.push('- Permission/role hierarchies (recursive permission check)');
  result.instructions.push('- Organization charts (reporting structure)');
  result.instructions.push('- Category trees (nested categories)');
  result.instructions.push('- Group memberships (user belongs to parent groups)');
}

async function scaffoldController(
  name: string,
  options: { namespace?: string; dryRun?: boolean; navRoute?: string; navRouteSuffix?: string } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const namespace = options?.namespace || (hierarchy.controllerArea
    ? `${config.conventions.namespaces.api}.Controllers.${hierarchy.controllerArea}`
    : `${config.conventions.namespaces.api}.Controllers`);
  const navRoute = options?.navRoute;
  const navRouteSuffix = options?.navRouteSuffix;

  // Determine route attribute based on navRoute option
  const routeAttribute = navRoute
    ? navRouteSuffix
      ? `[NavRoute("${navRoute}", Suffix = "${navRouteSuffix}")]`
      : `[NavRoute("${navRoute}")]`
    : `[Route("api/[controller]")]`;

  // Add using for NavRoute if used
  const navRouteUsing = navRoute ? 'using SmartStack.Api.Core.Routing;\n' : '';

  const controllerTemplate = `using Microsoft.AspNetCore.Authorization;
using Microsoft.AspNetCore.Mvc;
using Microsoft.Extensions.Logging;
${navRouteUsing}
namespace {{namespace}};

/// <summary>
/// API controller for {{name}} operations
/// </summary>
[ApiController]
{{routeAttribute}}
[Authorize]
public class {{name}}Controller : ControllerBase
{
    private readonly ILogger<{{name}}Controller> _logger;

    public {{name}}Controller(ILogger<{{name}}Controller> logger)
    {
        _logger = logger;
    }

    /// <summary>
    /// Get all {{nameLower}}s
    /// </summary>
    [HttpGet]
    public async Task<ActionResult<IEnumerable<{{name}}Dto>>> GetAll()
    {
        _logger.LogInformation("Getting all {{nameLower}}s");
        // TODO: Implement
        return Ok(Array.Empty<{{name}}Dto>());
    }

    /// <summary>
    /// Get {{nameLower}} by ID
    /// </summary>
    [HttpGet("{id:guid}")]
    public async Task<ActionResult<{{name}}Dto>> GetById(Guid id)
    {
        _logger.LogInformation("Getting {{nameLower}} {Id}", id);
        // TODO: Implement
        return NotFound();
    }

    /// <summary>
    /// Create new {{nameLower}}
    /// </summary>
    [HttpPost]
    public async Task<ActionResult<{{name}}Dto>> Create([FromBody] Create{{name}}Request request)
    {
        _logger.LogInformation("Creating {{nameLower}}");
        // TODO: Implement
        return CreatedAtAction(nameof(GetById), new { id = Guid.NewGuid() }, null);
    }

    /// <summary>
    /// Update {{nameLower}}
    /// </summary>
    [HttpPut("{id:guid}")]
    public async Task<ActionResult> Update(Guid id, [FromBody] Update{{name}}Request request)
    {
        _logger.LogInformation("Updating {{nameLower}} {Id}", id);
        // TODO: Implement
        return NoContent();
    }

    /// <summary>
    /// Delete {{nameLower}}
    /// </summary>
    [HttpDelete("{id:guid}")]
    public async Task<ActionResult> Delete(Guid id)
    {
        _logger.LogInformation("Deleting {{nameLower}} {Id}", id);
        // TODO: Implement
        return NoContent();
    }
}

// DTOs
public record {{name}}Dto(Guid Id, DateTime CreatedAt);
public record Create{{name}}Request();
public record Update{{name}}Request();
`;

  const context = {
    namespace,
    name,
    nameLower: name.charAt(0).toLowerCase() + name.slice(1),
    routeAttribute,
  };

  const controllerContent = Handlebars.compile(controllerTemplate)(context);

  const projectRoot = config.smartstack.projectPath;
  const apiPath = structure.api || path.join(projectRoot, 'Api');
  const controllersDir = hierarchy.controllerArea
    ? path.join(apiPath, 'Controllers', hierarchy.controllerArea)
    : path.join(apiPath, 'Controllers');

  const controllerFilePath = path.join(controllersDir, `${name}Controller.cs`);

  // Validate paths don't escape project directory
  validatePathSecurity(controllerFilePath, projectRoot);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(controllersDir);
    await writeText(controllerFilePath, controllerContent);
  }

  result.files.push({ path: controllerFilePath, content: controllerContent, type: 'created' });

  // Show appropriate route info based on NavRoute or traditional route
  if (navRoute) {
    result.instructions.push('Controller created with NavRoute (Navigation-based routing).');
    result.instructions.push(`NavRoute: ${navRoute}${navRouteSuffix ? ` (Suffix: ${navRouteSuffix})` : ''}`);
    result.instructions.push('');
    result.instructions.push('The actual API route will be resolved from Navigation entities at startup.');
    result.instructions.push('Ensure the navigation path exists in the database:');
    result.instructions.push(`  Context > Application > Module > Section matching "${navRoute}"`);
  } else {
    result.instructions.push('Controller created with traditional routing.');
    result.instructions.push('');
    result.instructions.push('⚠️  Consider using NavRoute for navigation-based routing:');
    result.instructions.push(`  [NavRoute("context.application.module")]`);
    result.instructions.push('');
    result.instructions.push('API endpoints (with traditional routing):');
    result.instructions.push(`  GET    /api/${name.toLowerCase()}`);
    result.instructions.push(`  GET    /api/${name.toLowerCase()}/{id}`);
    result.instructions.push(`  POST   /api/${name.toLowerCase()}`);
    result.instructions.push(`  PUT    /api/${name.toLowerCase()}/{id}`);
    result.instructions.push(`  DELETE /api/${name.toLowerCase()}/{id}`);
  }
}

async function scaffoldComponent(
  name: string,
  options: { namespace?: string; outputPath?: string; dryRun?: boolean; navRoute?: string } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const componentTemplate = `import React, { useState, useEffect } from 'react';

interface {{name}}Props {
  id?: string;
  onSave?: (data: {{name}}Data) => void;
  onCancel?: () => void;
}

interface {{name}}Data {
  id?: string;
  // TODO: Add {{name}} data properties
}

/**
 * {{name}} component
 */
export const {{name}}: React.FC<{{name}}Props> = ({ id, onSave, onCancel }) => {
  const [data, setData] = useState<{{name}}Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      // TODO: Fetch {{nameLower}} data
      setLoading(true);
      // fetch...
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (data && onSave) {
      onSave(data);
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">{{name}}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* TODO: Add form fields */}
        <div className="flex gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Save
          </button>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default {{name}};
`;

  // Hook template
  const hookTemplate = `import { useState, useEffect, useCallback } from 'react';
import { {{nameLower}}Api } from '../services/api/{{nameLower}}';

interface {{name}}Data {
  id?: string;
  // TODO: Add properties
}

interface Use{{name}}Options {
  id?: string;
  autoFetch?: boolean;
}

export function use{{name}}(options: Use{{name}}Options = {}) {
  const { id, autoFetch = true } = options;
  const [data, setData] = useState<{{name}}Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch{{name}} = useCallback(async (fetchId?: string) => {
    const targetId = fetchId || id;
    if (!targetId) return;

    setLoading(true);
    setError(null);
    try {
      // TODO: Implement API call
      // const result = await {{nameLower}}Api.getById(targetId);
      // setData(result);
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Unknown error'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const save{{name}} = useCallback(async (saveData: {{name}}Data) => {
    setLoading(true);
    setError(null);
    try {
      // TODO: Implement API call
      // const result = saveData.id
      //   ? await {{nameLower}}Api.update(saveData.id, saveData)
      //   : await {{nameLower}}Api.create(saveData);
      // setData(result);
      // return result;
    } catch (e) {
      setError(e instanceof Error ? e : new Error('Unknown error'));
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoFetch && id) {
      fetch{{name}}();
    }
  }, [autoFetch, id, fetch{{name}}]);

  return {
    data,
    loading,
    error,
    fetch: fetch{{name}},
    save: save{{name}},
    setData,
  };
}
`;

  const context = {
    name,
    nameLower: name.charAt(0).toLowerCase() + name.slice(1),
  };

  const componentContent = Handlebars.compile(componentTemplate)(context);
  const hookContent = Handlebars.compile(hookTemplate)(context);

  // Determine output paths — components in components/{context}/{module}/, hooks flat
  const projectRoot = config.smartstack.projectPath;
  // Fallback to 'web' folder (not hardcoded to smartstack-web)
  const webPath = structure.web || path.join(projectRoot, 'web');
  const componentsBase = path.join(webPath, 'src', 'components');
  const componentsPath = options?.outputPath
    ? options.outputPath
    : (hierarchy.context && hierarchy.module
        ? path.join(componentsBase, hierarchy.context.toLowerCase(), hierarchy.module.toLowerCase())
        : componentsBase);
  const hooksPath = path.join(webPath, 'src', 'hooks');

  const componentFilePath = path.join(componentsPath, `${name}.tsx`);
  const hookFilePath = path.join(hooksPath, `use${name}.ts`);

  // Validate paths don't escape project directory
  validatePathSecurity(componentFilePath, projectRoot);
  validatePathSecurity(hookFilePath, projectRoot);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(componentsPath);
    await ensureDirectory(hooksPath);
    await writeText(componentFilePath, componentContent);
    await writeText(hookFilePath, hookContent);
  }

  result.files.push({ path: componentFilePath, content: componentContent, type: 'created' });
  result.files.push({ path: hookFilePath, content: hookContent, type: 'created' });

  result.instructions.push('Import and use the component:');
  const componentImportPath = hierarchy.context && hierarchy.module
    ? `@/components/${hierarchy.context.toLowerCase()}/${hierarchy.module.toLowerCase()}/${name}`
    : `./components/${name}`;
  result.instructions.push(`import { ${name} } from '${componentImportPath}';`);
  result.instructions.push(`import { use${name} } from '@/hooks/use${name}';`);
}

async function scaffoldTest(
  name: string,
  options: { namespace?: string; isSystemEntity?: boolean; dryRun?: boolean } | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const isSystemEntity = options?.isSystemEntity || false;

  // xUnit Service Test template
  const serviceTestTemplate = `using System;
using System.Threading;
using System.Threading.Tasks;
using Microsoft.Extensions.Logging;
using Moq;
using Xunit;
using FluentAssertions;
using ${config.conventions.namespaces.application}.Services;

namespace ${config.conventions.namespaces.application}.Tests.Services;

/// <summary>
/// Unit tests for {{name}}Service
/// </summary>
public class {{name}}ServiceTests
{
    private readonly Mock<ILogger<{{name}}Service>> _loggerMock;
    private readonly {{name}}Service _sut;

    public {{name}}ServiceTests()
    {
        _loggerMock = new Mock<ILogger<{{name}}Service>>();
        _sut = new {{name}}Service(_loggerMock.Object);
    }

    [Fact]
    public async Task GetByIdAsync_ShouldReturnEntity_WhenExists()
    {
        // Arrange
        var id = Guid.NewGuid();
{{#unless isSystemEntity}}
        var tenantId = Guid.NewGuid();
{{/unless}}

        // Act
        var result = await _sut.GetByIdAsync(CancellationToken.None);

        // Assert
        // TODO: Implement actual assertion
        result.Should().NotBeNull();
    }

    [Fact]
    public async Task GetAllAsync_ShouldReturnList()
    {
        // Arrange
{{#unless isSystemEntity}}
        var tenantId = Guid.NewGuid();
{{/unless}}

        // Act
        var result = await _sut.GetAllAsync(CancellationToken.None);

        // Assert
        // TODO: Implement actual assertion
        result.Should().NotBeNull();
    }

    [Fact]
    public async Task CreateAsync_ShouldCreateEntity()
    {
        // Arrange
{{#unless isSystemEntity}}
        var tenantId = Guid.NewGuid();
{{/unless}}

        // Act
        var result = await _sut.CreateAsync(CancellationToken.None);

        // Assert
        // TODO: Implement actual assertion
        result.Should().NotBeNull();
    }

    [Fact]
    public async Task UpdateAsync_ShouldUpdateEntity_WhenExists()
    {
        // Arrange
        var id = Guid.NewGuid();

        // Act
        var result = await _sut.UpdateAsync(CancellationToken.None);

        // Assert
        // TODO: Implement actual assertion
        result.Should().NotBeNull();
    }

    [Fact]
    public async Task DeleteAsync_ShouldSoftDelete_WhenExists()
    {
        // Arrange
        var id = Guid.NewGuid();

        // Act
        var result = await _sut.DeleteAsync(CancellationToken.None);

        // Assert
        // TODO: Implement actual assertion
        result.Should().NotBeNull();
    }
}
`;

  const context = {
    name,
    isSystemEntity,
  };

  const testContent = Handlebars.compile(serviceTestTemplate)(context);

  // Determine output path (tests folder)
  const testsPath = structure.application
    ? path.join(path.dirname(structure.application), `${path.basename(structure.application)}.Tests`, 'Services')
    : path.join(config.smartstack.projectPath, 'Application.Tests', 'Services');

  const testFilePath = path.join(testsPath, `${name}ServiceTests.cs`);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(testsPath);
    await writeText(testFilePath, testContent);
  }

  result.files.push({ path: testFilePath, content: testContent, type: 'created' });

  result.instructions.push('Run tests:');
  result.instructions.push(`dotnet test --filter "FullyQualifiedName~${name}ServiceTests"`);
  result.instructions.push('');
  result.instructions.push('Required packages:');
  result.instructions.push('- xunit');
  result.instructions.push('- Moq');
  result.instructions.push('- FluentAssertions');
}

async function scaffoldDtos(
  name: string,
  options: FeatureOptions | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const namespace = options?.namespace || (hierarchy.domainPath
    ? `${config.conventions.namespaces.application}.${hierarchy.domainPath.replace(/[\\/]/g, '.')}.DTOs`
    : `${config.conventions.namespaces.application}.DTOs`);
  const isSystemEntity = options?.isSystemEntity || false;
  const properties = options?.entityProperties || [
    { name: 'Name', type: 'string', required: true, maxLength: 200 },
    { name: 'Description', type: 'string?', required: false, maxLength: 500 },
  ];

  // Response DTO template
  const responseDtoTemplate = `using System;

namespace {{namespace}};

/// <summary>
/// Response DTO for {{name}}
/// </summary>
public record {{name}}ResponseDto
{
    /// <summary>Unique identifier</summary>
    public Guid Id { get; init; }

{{#unless isSystemEntity}}
    /// <summary>Tenant identifier</summary>
    public Guid TenantId { get; init; }

{{/unless}}
    /// <summary>Unique code</summary>
    public string Code { get; init; } = string.Empty;

{{#each properties}}
    /// <summary>{{name}}</summary>
    public {{type}} {{name}} { get; init; }{{#if (eq type "string")}} = string.Empty;{{/if}}

{{/each}}
    /// <summary>Creation timestamp</summary>
    public DateTime CreatedAt { get; init; }

    /// <summary>Last update timestamp</summary>
    public DateTime? UpdatedAt { get; init; }

    /// <summary>Created by user</summary>
    public string? CreatedBy { get; init; }
}
`;

  // Create DTO template
  const createDtoTemplate = `using System;
using System.ComponentModel.DataAnnotations;

namespace {{namespace}};

/// <summary>
/// DTO for creating a new {{name}}
/// </summary>
public record Create{{name}}Dto
{
    /// <summary>Unique code (will be lowercased)</summary>
    [Required]
    [MaxLength(100)]
    public string Code { get; init; } = string.Empty;

{{#each properties}}
{{#if required}}
    /// <summary>{{name}} (required)</summary>
    [Required]
{{#if maxLength}}
    [MaxLength({{maxLength}})]
{{/if}}
    public {{type}} {{name}} { get; init; }{{#if (eq type "string")}} = string.Empty;{{/if}}

{{else}}
    /// <summary>{{name}} (optional)</summary>
{{#if maxLength}}
    [MaxLength({{maxLength}})]
{{/if}}
    public {{type}} {{name}} { get; init; }

{{/if}}
{{/each}}
}
`;

  // Update DTO template
  const updateDtoTemplate = `using System;
using System.ComponentModel.DataAnnotations;

namespace {{namespace}};

/// <summary>
/// DTO for updating an existing {{name}}
/// </summary>
public record Update{{name}}Dto
{
{{#each properties}}
{{#if required}}
    /// <summary>{{name}} (required)</summary>
    [Required]
{{#if maxLength}}
    [MaxLength({{maxLength}})]
{{/if}}
    public {{type}} {{name}} { get; init; }{{#if (eq type "string")}} = string.Empty;{{/if}}

{{else}}
    /// <summary>{{name}} (optional)</summary>
{{#if maxLength}}
    [MaxLength({{maxLength}})]
{{/if}}
    public {{type}} {{name}} { get; init; }

{{/if}}
{{/each}}
}
`;

  // Register eq helper for Handlebars
  Handlebars.registerHelper('eq', (a: string, b: string) => a === b);

  const context = {
    namespace,
    name,
    isSystemEntity,
    properties,
  };

  const responseContent = Handlebars.compile(responseDtoTemplate)(context);
  const createContent = Handlebars.compile(createDtoTemplate)(context);
  const updateContent = Handlebars.compile(updateDtoTemplate)(context);

  // Determine output paths — DTOs in Application/{Context}/{Module}/DTOs/
  const basePath = structure.application || config.smartstack.projectPath;
  const dtosPath = hierarchy.domainPath
    ? path.join(basePath, hierarchy.domainPath, 'DTOs')
    : path.join(basePath, 'DTOs', name);

  const responseFilePath = path.join(dtosPath, `${name}ResponseDto.cs`);
  const createFilePath = path.join(dtosPath, `Create${name}Dto.cs`);
  const updateFilePath = path.join(dtosPath, `Update${name}Dto.cs`);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(dtosPath);
    await writeText(responseFilePath, responseContent);
    await writeText(createFilePath, createContent);
    await writeText(updateFilePath, updateContent);
  }

  result.files.push({ path: responseFilePath, content: responseContent, type: 'created' });
  result.files.push({ path: createFilePath, content: createContent, type: 'created' });
  result.files.push({ path: updateFilePath, content: updateContent, type: 'created' });

  result.instructions.push('DTOs generated:');
  result.instructions.push(`- ${name}ResponseDto: For API responses`);
  result.instructions.push(`- Create${name}Dto: For POST requests`);
  result.instructions.push(`- Update${name}Dto: For PUT requests`);
}

async function scaffoldValidator(
  name: string,
  options: FeatureOptions | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const namespace = options?.namespace || (hierarchy.domainPath
    ? `${config.conventions.namespaces.application}.${hierarchy.domainPath.replace(/[\\/]/g, '.')}.Validators`
    : `${config.conventions.namespaces.application}.Validators`);
  const properties = options?.entityProperties || [
    { name: 'Name', type: 'string', required: true, maxLength: 200 },
    { name: 'Description', type: 'string?', required: false, maxLength: 500 },
  ];

  // Create validator template
  const createValidatorTemplate = `using FluentValidation;
using ${config.conventions.namespaces.application}.DTOs;

namespace {{namespace}};

/// <summary>
/// Validator for Create{{name}}Dto
/// </summary>
public class Create{{name}}DtoValidator : AbstractValidator<Create{{name}}Dto>
{
    public Create{{name}}DtoValidator()
    {
        RuleFor(x => x.Code)
            .NotEmpty().WithMessage("Code is required")
            .MaximumLength(100).WithMessage("Code must not exceed 100 characters")
            .Matches("^[a-z0-9_]+$").WithMessage("Code must be lowercase alphanumeric with underscores");

{{#each properties}}
{{#if required}}
        RuleFor(x => x.{{name}})
            .NotEmpty().WithMessage("{{name}} is required"){{#if maxLength}}
            .MaximumLength({{maxLength}}).WithMessage("{{name}} must not exceed {{maxLength}} characters"){{/if}};

{{else}}
{{#if maxLength}}
        RuleFor(x => x.{{name}})
            .MaximumLength({{maxLength}}).WithMessage("{{name}} must not exceed {{maxLength}} characters")
            .When(x => !string.IsNullOrEmpty(x.{{name}}));

{{/if}}
{{/if}}
{{/each}}
    }
}
`;

  // Update validator template
  const updateValidatorTemplate = `using FluentValidation;
using ${config.conventions.namespaces.application}.DTOs;

namespace {{namespace}};

/// <summary>
/// Validator for Update{{name}}Dto
/// </summary>
public class Update{{name}}DtoValidator : AbstractValidator<Update{{name}}Dto>
{
    public Update{{name}}DtoValidator()
    {
{{#each properties}}
{{#if required}}
        RuleFor(x => x.{{name}})
            .NotEmpty().WithMessage("{{name}} is required"){{#if maxLength}}
            .MaximumLength({{maxLength}}).WithMessage("{{name}} must not exceed {{maxLength}} characters"){{/if}};

{{else}}
{{#if maxLength}}
        RuleFor(x => x.{{name}})
            .MaximumLength({{maxLength}}).WithMessage("{{name}} must not exceed {{maxLength}} characters")
            .When(x => !string.IsNullOrEmpty(x.{{name}}));

{{/if}}
{{/if}}
{{/each}}
    }
}
`;

  const context = {
    namespace,
    name,
    properties,
  };

  const createValidatorContent = Handlebars.compile(createValidatorTemplate)(context);
  const updateValidatorContent = Handlebars.compile(updateValidatorTemplate)(context);

  // Determine output paths — Validators in Application/{Context}/{Module}/Validators/
  const basePath = structure.application || config.smartstack.projectPath;
  const validatorsPath = hierarchy.domainPath
    ? path.join(basePath, hierarchy.domainPath, 'Validators')
    : path.join(basePath, 'Validators');

  const createValidatorFilePath = path.join(validatorsPath, `Create${name}DtoValidator.cs`);
  const updateValidatorFilePath = path.join(validatorsPath, `Update${name}DtoValidator.cs`);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(validatorsPath);
    await writeText(createValidatorFilePath, createValidatorContent);
    await writeText(updateValidatorFilePath, updateValidatorContent);
  }

  result.files.push({ path: createValidatorFilePath, content: createValidatorContent, type: 'created' });
  result.files.push({ path: updateValidatorFilePath, content: updateValidatorContent, type: 'created' });

  result.instructions.push('Register validators in DI:');
  result.instructions.push(`services.AddValidatorsFromAssemblyContaining<Create${name}DtoValidator>();`);
  result.instructions.push('');
  result.instructions.push('Required package: FluentValidation.DependencyInjectionExtensions');
}

async function scaffoldRepository(
  name: string,
  options: FeatureOptions | undefined,
  structure: ReturnType<typeof findSmartStackStructure> extends Promise<infer T> ? T : never,
  config: Config,
  result: ScaffoldResult,
  dryRun: boolean = false
): Promise<void> {
  const hierarchy = resolveHierarchy(options?.navRoute);
  const isSystemEntity = options?.isSystemEntity || false;
  const schema = options?.schema || config.conventions.schemas.platform;
  const dbContextName = schema === 'extensions' ? 'ExtensionsDbContext' : 'CoreDbContext';

  // Repository interface template
  const interfaceTemplate = `using System;
using System.Collections.Generic;
using System.Threading;
using System.Threading.Tasks;
using ${config.conventions.namespaces.domain};

namespace ${config.conventions.namespaces.application}.Repositories;

/// <summary>
/// Repository interface for {{name}} entity
/// </summary>
public interface I{{name}}Repository
{
    /// <summary>Get entity by ID</summary>
    Task<{{name}}?> GetByIdAsync(Guid id, CancellationToken ct = default);

    /// <summary>Get entity by code</summary>
    Task<{{name}}?> GetByCodeAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}string code, CancellationToken ct = default);

    /// <summary>Get all entities{{#unless isSystemEntity}} for tenant{{/unless}}</summary>
    Task<IReadOnlyList<{{name}}>> GetAllAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}CancellationToken ct = default);

    /// <summary>Check if code exists{{#unless isSystemEntity}} in tenant{{/unless}}</summary>
    Task<bool> ExistsAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}string code, CancellationToken ct = default);

    /// <summary>Add new entity</summary>
    Task<{{name}}> AddAsync({{name}} entity, CancellationToken ct = default);

    /// <summary>Update entity</summary>
    Task UpdateAsync({{name}} entity, CancellationToken ct = default);

    /// <summary>Soft delete entity</summary>
    Task DeleteAsync({{name}} entity, CancellationToken ct = default);
}
`;

  // Repository implementation template
  const implementationTemplate = `using System;
using System.Collections.Generic;
using System.Linq;
using System.Threading;
using System.Threading.Tasks;
using Microsoft.EntityFrameworkCore;
using ${config.conventions.namespaces.domain};
using ${config.conventions.namespaces.infrastructure}.Persistence;

namespace ${config.conventions.namespaces.infrastructure}.Repositories;

/// <summary>
/// Repository implementation for {{name}} entity
/// </summary>
public class {{name}}Repository : I{{name}}Repository
{
    private readonly {{dbContextName}} _context;

    public {{name}}Repository({{dbContextName}} context)
    {
        _context = context;
    }

    /// <inheritdoc />
    public async Task<{{name}}?> GetByIdAsync(Guid id, CancellationToken ct = default)
    {
        return await _context.{{name}}s
            .FirstOrDefaultAsync(e => e.Id == id, ct);
    }

    /// <inheritdoc />
    public async Task<{{name}}?> GetByCodeAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}string code, CancellationToken ct = default)
    {
        return await _context.{{name}}s
            .FirstOrDefaultAsync(e => {{#unless isSystemEntity}}e.TenantId == tenantId && {{/unless}}e.Code == code.ToLowerInvariant(), ct);
    }

    /// <inheritdoc />
    public async Task<IReadOnlyList<{{name}}>> GetAllAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}CancellationToken ct = default)
    {
        return await _context.{{name}}s
            {{#unless isSystemEntity}}.Where(e => e.TenantId == tenantId){{/unless}}
            .OrderBy(e => e.Code)
            .ToListAsync(ct);
    }

    /// <inheritdoc />
    public async Task<bool> ExistsAsync({{#unless isSystemEntity}}Guid tenantId, {{/unless}}string code, CancellationToken ct = default)
    {
        return await _context.{{name}}s
            .AnyAsync(e => {{#unless isSystemEntity}}e.TenantId == tenantId && {{/unless}}e.Code == code.ToLowerInvariant(), ct);
    }

    /// <inheritdoc />
    public async Task<{{name}}> AddAsync({{name}} entity, CancellationToken ct = default)
    {
        await _context.{{name}}s.AddAsync(entity, ct);
        await _context.SaveChangesAsync(ct);
        return entity;
    }

    /// <inheritdoc />
    public async Task UpdateAsync({{name}} entity, CancellationToken ct = default)
    {
        _context.{{name}}s.Update(entity);
        await _context.SaveChangesAsync(ct);
    }

    /// <inheritdoc />
    public async Task DeleteAsync({{name}} entity, CancellationToken ct = default)
    {
        entity.SoftDelete();
        await UpdateAsync(entity, ct);
    }
}
`;

  const context = {
    name,
    isSystemEntity,
    dbContextName,
  };

  const interfaceContent = Handlebars.compile(interfaceTemplate)(context);
  const implementationContent = Handlebars.compile(implementationTemplate)(context);

  // Determine output paths — Repository interface in Application, impl in Infrastructure/Repositories/{Module}/
  const appPath = structure.application || config.smartstack.projectPath;
  const infraPath = structure.infrastructure || path.join(config.smartstack.projectPath, 'Infrastructure');

  const appRepoDir = hierarchy.infraPath
    ? path.join(appPath, 'Repositories', hierarchy.infraPath)
    : path.join(appPath, 'Repositories');
  const infraRepoDir = hierarchy.infraPath
    ? path.join(infraPath, 'Repositories', hierarchy.infraPath)
    : path.join(infraPath, 'Repositories');

  const interfaceFilePath = path.join(appRepoDir, `I${name}Repository.cs`);
  const implementationFilePath = path.join(infraRepoDir, `${name}Repository.cs`);

  // Write files (or preview in dry-run mode)
  if (!dryRun) {
    await ensureDirectory(appRepoDir);
    await ensureDirectory(infraRepoDir);
    await writeText(interfaceFilePath, interfaceContent);
    await writeText(implementationFilePath, implementationContent);
  }

  result.files.push({ path: interfaceFilePath, content: interfaceContent, type: 'created' });
  result.files.push({ path: implementationFilePath, content: implementationContent, type: 'created' });

  result.instructions.push('Register repository in DI:');
  result.instructions.push(`services.AddScoped<I${name}Repository, ${name}Repository>();`);
}

function formatResult(result: ScaffoldResult, type: string, name: string, dryRun: boolean = false): string {
  const lines: string[] = [];

  if (dryRun) {
    lines.push(`# 🔍 DRY-RUN: Scaffold ${type}: ${name}`);
    lines.push('');
    lines.push('> **Preview mode**: No files were written. Review the generated code below.');
    lines.push('> To create files, run without `dryRun: true`');
    lines.push('');
  } else {
    lines.push(`# Scaffold ${type}: ${name}`);
    lines.push('');
  }

  if (result.success) {
    lines.push(dryRun ? '## 📄 Files to Generate' : '## ✅ Files Generated');
    lines.push('');

    for (const file of result.files) {
      lines.push(`### ${file.type === 'created' ? '📄' : '✏️'} ${path.basename(file.path)}`);
      lines.push(`**Path**: \`${file.path}\``);
      lines.push('');
      lines.push('```' + (file.path.endsWith('.cs') ? 'csharp' : 'typescript'));
      // Show first 50 lines of content
      const contentLines = file.content.split('\n').slice(0, 50);
      lines.push(contentLines.join('\n'));
      if (file.content.split('\n').length > 50) {
        lines.push('// ... (truncated)');
      }
      lines.push('```');
      lines.push('');
    }

    if (result.instructions.length > 0) {
      lines.push('## 📋 Next Steps');
      lines.push('');
      for (const instruction of result.instructions) {
        if (instruction.startsWith('services.') || instruction.startsWith('public DbSet')) {
          lines.push('```csharp');
          lines.push(instruction);
          lines.push('```');
        } else if (instruction.startsWith('dotnet ')) {
          lines.push('```bash');
          lines.push(instruction);
          lines.push('```');
        } else if (instruction.startsWith('import ')) {
          lines.push('```typescript');
          lines.push(instruction);
          lines.push('```');
        } else {
          lines.push(instruction);
        }
      }
    }
  } else {
    lines.push('## ❌ Generation Failed');
    lines.push('');
    for (const instruction of result.instructions) {
      lines.push(`- ${instruction}`);
    }
  }

  return lines.join('\n');
}
